/**
 * Per-view registry mapping read-along word/sentence keys to their rendered
 * elements, so the highlight manager can toggle `data-active` on exactly the
 * right nodes instead of running document-wide `querySelectorAll` every tick.
 *
 * Generic over the element type (`TEl`) so the core is DOM-free and node-
 * testable; the web view instantiates it with `HTMLElement`. One instance per
 * reader view (a `useRef` in `useReaderCommon` / `useDocCommon`).
 */

import { docPackCharRangesOverlap } from '@listening/frontend/doc-pack/text-parts';

export type LegacyWordKey = { chunkId: string; wordIdxInChunk: number };
export type LegacySentenceKey = {
  chapterId: string;
  paragraphIdx: number;
  sentenceIdx: number;
};

export type WordElementRegistry<TEl> = {
  /** All `register*` return an unregister cleanup (React 19 ref-callback cleanup).
   * `sentenceKey` (PDF bbox words) lets the manager resolve the active word's
   * sentence element for the follow anchor. */
  registerLegacyWord: (
    key: LegacyWordKey,
    el: TEl,
    sentenceKey?: LegacySentenceKey,
  ) => () => void;
  registerLegacySentence: (key: LegacySentenceKey, el: TEl) => () => void;
  registerDocPackWord: (
    page: number,
    chars: readonly [number, number],
    el: TEl,
  ) => () => void;
  /** Multi-rect words register several elements under one key. */
  getLegacyWordEls: (key: LegacyWordKey) => ReadonlyArray<TEl>;
  /** Every registered word of a chunk (for the untimed-word rule: words past the
   * alignment count stay stamped while their chunk plays). */
  getLegacyChunkWords: (chunkId: string) => ReadonlyArray<{
    wordIdxInChunk: number;
    els: ReadonlyArray<TEl>;
    sentenceKey?: LegacySentenceKey | undefined;
  }>;
  getLegacySentenceEl: (key: LegacySentenceKey) => TEl | undefined;
  getDocPackWordEls: (
    page: number,
    chars: readonly [number, number],
  ) => ReadonlyArray<TEl>;
  /** Bumped on every register/unregister → the manager re-applies when the
   * virtualizer mounts the active word after the position already settled. */
  readonly version: number;
};

const legacySentenceKeyId = (key: LegacySentenceKey) =>
  `${key.chapterId}:${key.paragraphIdx.toString()}:${key.sentenceIdx.toString()}`;

type DocPackPageEntries<TEl> = {
  /** Element → its char range, for unregister + rebuild. */
  readonly byEl: Map<TEl, readonly [number, number]>;
  /** Sorted-by-charStart cache; rebuilt lazily when `dirty`. Each entry carries
   * the running max charEnd over the prefix so overlap scans can stop early —
   * correct even when char ranges overlap. */
  sorted: ReadonlyArray<{
    chars: readonly [number, number];
    prefMaxEnd: number;
    el: TEl;
  }>;
  dirty: boolean;
};

type LegacyWordEntry<TEl> = {
  readonly els: Set<TEl>;
  sentenceKey?: LegacySentenceKey;
};

export function createWordElementRegistry<TEl>(): WordElementRegistry<TEl> {
  // chunkId → wordIdxInChunk → { elements (multi-rect words register several),
  // sentence key }.
  const legacyWords = new Map<string, Map<number, LegacyWordEntry<TEl>>>();
  const legacySentences = new Map<string, Set<TEl>>();
  const docPackPages = new Map<number, DocPackPageEntries<TEl>>();
  let version = 0;

  const addToSetMap = (map: Map<string, Set<TEl>>, id: string, el: TEl) => {
    let set = map.get(id);
    if (!set) {
      set = new Set();
      map.set(id, set);
    }
    set.add(el);
    version++;
  };

  const removeFromSetMap = (
    map: Map<string, Set<TEl>>,
    id: string,
    el: TEl,
  ) => {
    const set = map.get(id);
    if (!set) return;
    set.delete(el);
    if (set.size === 0) map.delete(id);
    version++;
  };

  return {
    registerLegacyWord(key, el, sentenceKey) {
      let byIdx = legacyWords.get(key.chunkId);
      if (!byIdx) {
        byIdx = new Map();
        legacyWords.set(key.chunkId, byIdx);
      }
      let entry = byIdx.get(key.wordIdxInChunk);
      if (!entry) {
        entry = { els: new Set() };
        byIdx.set(key.wordIdxInChunk, entry);
      }
      entry.els.add(el);
      if (sentenceKey) entry.sentenceKey = sentenceKey;
      version++;
      return () => {
        const idxMap = legacyWords.get(key.chunkId);
        const e = idxMap?.get(key.wordIdxInChunk);
        if (!idxMap || !e) return;
        e.els.delete(el);
        if (e.els.size === 0) idxMap.delete(key.wordIdxInChunk);
        if (idxMap.size === 0) legacyWords.delete(key.chunkId);
        version++;
      };
    },
    registerLegacySentence(key, el) {
      const id = legacySentenceKeyId(key);
      addToSetMap(legacySentences, id, el);
      return () => removeFromSetMap(legacySentences, id, el);
    },
    registerDocPackWord(page, chars, el) {
      let entries = docPackPages.get(page);
      if (!entries) {
        entries = { byEl: new Map(), sorted: [], dirty: true };
        docPackPages.set(page, entries);
      }
      entries.byEl.set(el, chars);
      entries.dirty = true;
      version++;
      return () => {
        const e = docPackPages.get(page);
        if (!e) return;
        e.byEl.delete(el);
        e.dirty = true;
        if (e.byEl.size === 0) docPackPages.delete(page);
        version++;
      };
    },
    getLegacyWordEls(key) {
      const entry = legacyWords.get(key.chunkId)?.get(key.wordIdxInChunk);
      return entry ? [...entry.els] : [];
    },
    getLegacyChunkWords(chunkId) {
      const byIdx = legacyWords.get(chunkId);
      if (!byIdx) return [];
      return [...byIdx.entries()].map(([wordIdxInChunk, entry]) => ({
        wordIdxInChunk,
        els: [...entry.els],
        sentenceKey: entry.sentenceKey,
      }));
    },
    getLegacySentenceEl(key) {
      const set = legacySentences.get(legacySentenceKeyId(key));
      if (!set) return undefined;
      for (const el of set) return el;
      return undefined;
    },
    getDocPackWordEls(page, chars) {
      const entries = docPackPages.get(page);
      if (!entries) return [];
      if (entries.dirty) {
        let prefMaxEnd = Number.NEGATIVE_INFINITY;
        entries.sorted = [...entries.byEl.entries()]
          .map(([el, c]) => ({ chars: c, el }))
          .toSorted((a, b) => a.chars[0] - b.chars[0])
          .map((e) => {
            prefMaxEnd = Math.max(prefMaxEnd, e.chars[1]);
            return { ...e, prefMaxEnd };
          });
        entries.dirty = false;
      }
      // Overlapping entries all have charStart < chars[1]; binary-search that
      // upper bound, then scan the window backward. `prefMaxEnd` lets us stop as
      // soon as no earlier entry can reach the query start — O(log n + k) for
      // non-overlapping words, still correct when they overlap.
      const sorted = entries.sorted;
      let lo = 0;
      let hi = sorted.length;
      while (lo < hi) {
        const mid = Math.floor((lo + hi) / 2);
        const entry = sorted[mid];
        if (entry && entry.chars[0] < chars[1]) lo = mid + 1;
        else hi = mid;
      }
      const out: TEl[] = [];
      for (let i = lo - 1; i >= 0; i--) {
        const entry = sorted[i];
        if (!entry) continue;
        if (entry.prefMaxEnd <= chars[0]) break;
        if (docPackCharRangesOverlap(entry.chars, chars)) out.push(entry.el);
      }
      return out;
    },
    get version() {
      return version;
    },
  };
}
